<template>
  <section class="home-shell relative overflow-hidden">
    <div class="pointer-events-none absolute inset-0 overflow-hidden">
      <div class="absolute -left-20 top-8 h-72 w-72 rounded-full bg-neon-400/12 blur-3xl"></div>
      <div class="absolute right-[-8rem] top-[22rem] h-96 w-96 rounded-full bg-amber-300/10 blur-3xl"></div>
      <div class="absolute left-1/3 top-[56rem] h-80 w-80 rounded-full bg-sky-400/10 blur-3xl"></div>
    </div>

    <div class="relative mx-auto flex max-w-6xl flex-col gap-16 px-6 py-12 sm:py-16 lg:gap-24">
      <div class="grid items-center gap-10 lg:grid-cols-[minmax(0,1.02fr)_minmax(320px,0.98fr)] lg:gap-14">
        <div class="max-w-3xl">
          <div class="flex flex-wrap gap-2">
            <span
              v-for="chip in heroChips"
              :key="chip"
              class="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-steel-300"
            >
              {{ chip }}
            </span>
          </div>

          <p class="mt-6 text-xs uppercase tracking-[0.38em] text-steel-400">{{ $t('home.eyebrow') }}</p>
          <h1 class="mt-5 max-w-4xl font-display text-4xl font-semibold tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
            {{ $t('home.title') }}
          </h1>
          <p class="mt-6 max-w-2xl text-base leading-7 text-steel-200/90 sm:text-lg">
            {{ $t('home.description') }}
          </p>

          <div class="mt-8 flex flex-wrap gap-4">
            <router-link
              class="inline-flex items-center justify-center rounded-2xl bg-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.28em] text-midnight-900 transition hover:bg-neon-400"
              to="/register"
            >
              {{ $t('home.becomeMember') }}
            </router-link>
            <router-link
              class="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold uppercase tracking-[0.28em] text-white transition hover:border-white/30 hover:bg-white/10"
              to="/login"
            >
              {{ $t('home.memberLogin') }}
            </router-link>
          </div>

          <div class="mt-10 grid gap-4 sm:grid-cols-3">
            <article
              v-for="stat in heroStats"
              :key="stat.label"
              class="rounded-[1.75rem] border border-white/10 bg-white/[0.03] p-4 shadow-glow"
            >
              <p class="text-[10px] uppercase tracking-[0.28em] text-steel-400">{{ stat.label }}</p>
              <p class="mt-3 font-display text-2xl font-semibold text-white">{{ stat.value }}</p>
              <p class="mt-2 text-sm leading-6 text-steel-300">{{ stat.caption }}</p>
            </article>
          </div>
        </div>

        <article class="home-panel home-panel-featured rounded-[2rem] p-5">
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="text-[10px] uppercase tracking-[0.3em] text-amber-200/85">{{ $t('home.buildWeekEyebrow') }}</p>
              <h2 class="mt-2 font-display text-2xl font-semibold text-white">{{ $t('home.buildWeekTitle') }}</h2>
            </div>
            <span class="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-steel-300">
              {{ $t('home.previewSpotlightTag') }}
            </span>
          </div>

          <router-link
            v-if="buildOfWeek"
            :to="`/post/${buildOfWeek.id}`"
            class="mt-5 block overflow-hidden rounded-[1.6rem] border border-white/10 bg-white/[0.03] transition hover:border-white/20"
          >
            <div class="aspect-[16/10] overflow-hidden bg-black/40">
              <img
                :src="currentMedia(buildOfWeek)"
                :style="{ objectPosition: currentMediaPosition(buildOfWeek) }"
                alt="Build of the week"
                class="h-full w-full object-cover"
              />
            </div>
            <div class="space-y-4 p-4">
              <div class="flex items-center gap-3">
                <div class="h-11 w-11 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                  <img
                    v-if="buildOfWeek.authorAvatar"
                    :src="buildOfWeek.authorAvatar"
                    alt=""
                    class="h-full w-full object-cover"
                    referrerpolicy="no-referrer"
                    crossorigin="anonymous"
                  />
                  <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ buildOfWeek.authorInitials }}</div>
                </div>
                <div class="min-w-0">
                  <div class="flex items-center gap-1.5">
                    <p class="truncate text-sm font-semibold text-white">{{ buildOfWeek.authorName }}</p>
                    <verified-badge v-if="buildOfWeek.authorVerified" size="xs" />
                  </div>
                  <p class="text-xs text-steel-400">@{{ buildOfWeek.authorHandle }}</p>
                </div>
              </div>

              <div>
                <p class="text-base font-semibold text-white">{{ buildOfWeek.caption || $t('home.buildWeekFallbackCaption') }}</p>
                <p class="mt-2 text-sm leading-6 text-steel-200/90">{{ buildOfWeek.weekScore }} {{ $t('home.buildWeekScore') }}</p>
              </div>

              <div class="flex flex-wrap gap-2 text-xs text-steel-300">
                <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">{{ buildOfWeek.likeCount }} {{ $t('home.metricLikes') }}</span>
                <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">{{ buildOfWeek.viewCount }} {{ $t('home.metricViews') }}</span>
                <span v-if="buildOfWeek.carLabel" class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
                  {{ buildOfWeek.carLabel }}
                </span>
              </div>
            </div>
          </router-link>

          <div v-else-if="contentLoading" class="mt-5 space-y-4 rounded-[1.6rem] border border-white/10 bg-white/[0.03] p-4 animate-pulse">
            <div class="aspect-[16/10] rounded-[1.25rem] bg-white/10"></div>
            <div class="h-5 w-40 rounded bg-white/10"></div>
            <div class="h-4 w-full rounded bg-white/10"></div>
            <div class="h-4 w-2/3 rounded bg-white/10"></div>
          </div>

          <div v-else class="mt-5 rounded-[1.6rem] border border-dashed border-white/10 px-5 py-8 text-sm text-steel-400">
            {{ $t('home.buildWeekEmpty') }}
          </div>
        </article>
      </div>

      <section class="space-y-8">
        <div class="max-w-3xl">
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('home.liveEyebrow') }}</p>
          <h2 class="mt-3 font-display text-3xl font-semibold tracking-[-0.03em] text-white sm:text-4xl">
            {{ $t('home.liveTitle') }}
          </h2>
          <p class="mt-4 text-base leading-7 text-steel-200/85">
            {{ $t('home.liveDescription') }}
          </p>
        </div>

        <div class="grid gap-5 xl:grid-cols-[1.05fr_0.95fr]">
          <article class="home-panel rounded-[2rem] p-6">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('home.membersWeekEyebrow') }}</p>
                <h3 class="mt-2 font-display text-2xl font-semibold text-white">{{ $t('home.membersWeekTitle') }}</h3>
              </div>
              <span class="max-w-[14rem] text-xs leading-5 text-steel-400 sm:text-right">
                {{ $t('home.membersWeekDescription') }}
              </span>
            </div>

            <div v-if="topMembers.length" class="mt-5 space-y-3">
              <router-link
                v-for="member in topMembers"
                :key="member.id"
                :to="`/u/${member.handle}`"
                class="home-rank-row relative grid grid-cols-[auto_minmax(0,1fr)] gap-3 rounded-[1.25rem] border border-white/10 bg-white/[0.03] px-3 py-3 transition hover:border-white/20 sm:flex sm:items-center"
              >
                <div
                  class="absolute -left-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold"
                  :class="member.rank === 1 ? 'bg-yellow-400 text-black' : member.rank === 2 ? 'bg-slate-300 text-black' : member.rank === 3 ? 'bg-amber-700 text-white' : 'bg-white/5 text-white'"
                >
                  {{ member.rank }}
                </div>
                <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                  <img
                    v-if="member.avatar"
                    :src="member.avatar"
                    alt=""
                    class="h-full w-full object-cover"
                    referrerpolicy="no-referrer"
                    crossorigin="anonymous"
                  />
                  <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ member.initials }}</div>
                </div>
                <div class="min-w-0">
                  <div class="flex items-center gap-1.5">
                    <p class="truncate text-sm font-semibold text-white">{{ member.fullName }}</p>
                    <verified-badge v-if="member.verified" size="xs" />
                  </div>
                  <p class="text-xs text-steel-400">@{{ member.handle }}</p>
                </div>
                <div class="col-span-2 flex items-center justify-between border-t border-white/5 pt-2 sm:col-span-1 sm:ml-auto sm:block sm:border-t-0 sm:pt-0 sm:text-right">
                  <p class="text-sm font-semibold text-white">{{ member.score }}</p>
                  <p class="text-[10px] uppercase tracking-[0.22em] text-steel-400">{{ member.likes }} {{ $t('home.metricLikes') }}</p>
                </div>
              </router-link>
            </div>

            <div v-else-if="contentLoading" class="mt-5 space-y-3">
              <div v-for="item in 4" :key="`member-skeleton-${item}`" class="h-20 rounded-[1.25rem] border border-white/10 bg-white/[0.03] animate-pulse"></div>
            </div>

            <p v-else class="mt-5 text-sm text-steel-400">{{ $t('home.membersWeekEmpty') }}</p>
          </article>

          <div class="grid gap-5">
            <article class="home-panel rounded-[2rem] p-6">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('home.garageEyebrow') }}</p>
                  <h3 class="mt-2 font-display text-2xl font-semibold text-white">{{ $t('home.garageTitle') }}</h3>
                </div>
                <span v-if="featuredGarageBuild" class="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-steel-300">
                  {{ featuredGarageBuild.buildStatusLabel }}
                </span>
              </div>

              <router-link
                v-if="featuredGarageBuild"
                :to="`/u/${featuredGarageBuild.ownerHandle}`"
                class="mt-5 block overflow-hidden rounded-[1.6rem] border border-white/10 bg-white/[0.03] transition hover:border-white/20"
              >
                <div class="aspect-[16/9] overflow-hidden bg-black/30">
                  <img
                    v-if="featuredGarageBuild.coverImage"
                    :src="featuredGarageBuild.coverImage"
                    :alt="featuredGarageBuild.title"
                    class="h-full w-full object-cover"
                  />
                  <div v-else class="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(125,249,255,0.16),_transparent_36%),linear-gradient(180deg,rgba(255,255,255,0.03),rgba(0,0,0,0.16))] text-xs uppercase tracking-[0.35em] text-steel-400">
                    {{ $t('home.garageTitle') }}
                  </div>
                </div>
                <div class="space-y-4 p-4">
                  <div class="flex items-center gap-3">
                    <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                      <img
                        v-if="featuredGarageBuild.ownerAvatar"
                        :src="featuredGarageBuild.ownerAvatar"
                        alt=""
                        class="h-full w-full object-cover"
                        referrerpolicy="no-referrer"
                        crossorigin="anonymous"
                      />
                      <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ featuredGarageBuild.ownerInitials }}</div>
                    </div>
                    <div class="min-w-0">
                      <div class="flex items-center gap-1.5">
                        <p class="truncate text-sm font-semibold text-white">{{ featuredGarageBuild.ownerName }}</p>
                        <verified-badge v-if="featuredGarageBuild.ownerVerified" size="xs" />
                      </div>
                      <p class="text-xs text-steel-400">@{{ featuredGarageBuild.ownerHandle }}</p>
                    </div>
                  </div>

                  <div>
                    <p class="text-base font-semibold text-white">{{ featuredGarageBuild.title }}</p>
                    <p class="mt-2 text-sm text-steel-300">{{ featuredGarageBuild.subtitle }}</p>
                  </div>

                  <div class="grid gap-3 sm:grid-cols-2">
                    <div class="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
                      <p class="text-[10px] uppercase tracking-[0.24em] text-steel-400">{{ $t('home.garagePowerLabel') }}</p>
                      <p class="mt-2 text-sm font-semibold text-white">{{ featuredGarageBuild.powerLabel }}</p>
                    </div>
                    <div class="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
                      <p class="text-[10px] uppercase tracking-[0.24em] text-steel-400">{{ $t('home.garageDrivetrainLabel') }}</p>
                      <p class="mt-2 text-sm font-semibold text-white">{{ featuredGarageBuild.drivetrainLabel }}</p>
                    </div>
                  </div>
                </div>
              </router-link>

              <div v-else-if="contentLoading" class="mt-5 h-64 rounded-[1.6rem] border border-white/10 bg-white/[0.03] animate-pulse"></div>
              <p v-else class="mt-5 text-sm text-steel-400">{{ $t('home.garageEmpty') }}</p>
            </article>

            <article class="home-panel rounded-[2rem] p-6">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('home.meetsEyebrow') }}</p>
                  <h3 class="mt-2 font-display text-2xl font-semibold text-white">{{ $t('home.meetsTitle') }}</h3>
                </div>
                <router-link
                  to="/meets"
                  class="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-steel-300 transition hover:border-white/20 hover:text-white"
                >
                  {{ $t('meetsPage.viewEvent') }}
                </router-link>
              </div>

              <div v-if="latestLocalMeets.length" class="mt-5 space-y-3">
                <router-link
                  v-for="meet in latestLocalMeets"
                  :key="meet.id"
                  :to="`/meets/${meet.id}`"
                  class="block overflow-hidden rounded-[1.4rem] border border-white/10 bg-white/[0.03] transition hover:border-white/20"
                >
                  <div class="grid gap-0 sm:grid-cols-[140px_minmax(0,1fr)]">
                    <div class="h-32 overflow-hidden bg-black/30 sm:h-full">
                      <img
                        v-if="meet.coverImage"
                        :src="meet.coverImage"
                        :style="{ objectPosition: meet.coverPosition }"
                        :alt="meet.title"
                        class="h-full w-full object-cover"
                      />
                      <div v-else class="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.18),_transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.03),rgba(0,0,0,0.15))] text-[10px] uppercase tracking-[0.3em] text-steel-400">
                        {{ $t('meetsPage.eventFallback') }}
                      </div>
                    </div>
                    <div class="space-y-3 p-4">
                      <div class="flex flex-wrap gap-2">
                        <span class="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-steel-300">{{ meet.eventTypeLabel }}</span>
                        <span class="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-steel-300">{{ meet.startsAtLabel }}</span>
                      </div>
                      <div>
                        <p class="text-base font-semibold text-white">{{ meet.title }}</p>
                        <p class="mt-2 text-sm leading-6 text-steel-300">{{ meet.locationLabel }}</p>
                      </div>
                      <div class="flex items-center gap-3">
                        <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                          <img
                            v-if="meet.hostAvatar"
                            :src="meet.hostAvatar"
                            alt=""
                            class="h-full w-full object-cover"
                            referrerpolicy="no-referrer"
                            crossorigin="anonymous"
                          />
                          <div v-else class="flex h-full w-full items-center justify-center text-xs font-semibold text-steel-200">{{ meet.hostInitials }}</div>
                        </div>
                        <p class="text-xs uppercase tracking-[0.18em] text-steel-400">@{{ meet.hostHandle }}</p>
                      </div>
                    </div>
                  </div>
                </router-link>
              </div>

              <div v-else-if="contentLoading" class="mt-5 space-y-3">
                <div v-for="item in 2" :key="`meet-skeleton-${item}`" class="h-32 rounded-[1.4rem] border border-white/10 bg-white/[0.03] animate-pulse"></div>
              </div>

              <p v-else class="mt-5 text-sm text-steel-400">{{ $t('home.meetsEmpty') }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="space-y-8">
        <div class="max-w-2xl">
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('home.featureEyebrow') }}</p>
          <h2 class="mt-3 font-display text-3xl font-semibold tracking-[-0.03em] text-white sm:text-4xl">
            {{ $t('home.featureTitle') }}
          </h2>
          <p class="mt-4 text-base leading-7 text-steel-200/85">
            {{ $t('home.featureDescription') }}
          </p>
        </div>
        <div class="grid gap-5 lg:grid-cols-3">
          <article
            v-for="card in featureCards"
            :key="card.title"
            class="home-panel rounded-[2rem] p-6"
          >
            <div class="flex items-center justify-between gap-3">
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ card.eyebrow }}</p>
              <span class="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-steel-300">
                {{ card.tag }}
              </span>
            </div>
            <h3 class="mt-4 font-display text-2xl font-semibold text-white">{{ card.title }}</h3>
            <p class="mt-4 text-sm leading-6 text-steel-200/90">{{ card.body }}</p>
            <ul class="mt-6 space-y-3">
              <li
                v-for="point in card.points"
                :key="point"
                class="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-steel-300"
              >
                <span class="mt-1 h-2 w-2 rounded-full bg-neon-400"></span>
                <span>{{ point }}</span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section class="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <article class="home-panel rounded-[2rem] p-6 sm:p-7">
          <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('home.storyEyebrow') }}</p>
          <h2 class="mt-3 max-w-2xl font-display text-3xl font-semibold tracking-[-0.03em] text-white sm:text-4xl">
            {{ $t('home.storyTitle') }}
          </h2>
          <p class="mt-4 max-w-2xl text-base leading-7 text-steel-200/85">
            {{ $t('home.storyDescription') }}
          </p>

          <div class="mt-8 space-y-4">
            <article
              v-for="story in storytellingCards"
              :key="story.title"
              class="rounded-[1.6rem] border border-white/10 bg-white/[0.03] p-5"
            >
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ story.eyebrow }}</p>
                  <h3 class="mt-2 text-lg font-semibold text-white">{{ story.title }}</h3>
                </div>
                <span class="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-steel-300">
                  {{ story.badge }}
                </span>
              </div>
              <p class="mt-3 text-sm leading-6 text-steel-200/90">{{ story.body }}</p>
            </article>
          </div>
        </article>

        <div class="grid gap-5">
          <article class="home-panel rounded-[2rem] p-6">
            <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('home.membershipEyebrow') }}</p>
            <h3 class="mt-3 font-display text-2xl font-semibold text-white">{{ $t('home.membershipTitle') }}</h3>
            <div class="mt-6 space-y-3">
              <div
                v-for="step in membershipSteps"
                :key="step.title"
                class="rounded-[1.4rem] border border-white/10 bg-black/20 p-4"
              >
                <div class="flex items-center gap-3">
                  <span class="flex h-8 w-8 items-center justify-center rounded-full border border-neon-400/30 bg-neon-400/10 text-xs font-semibold text-neon-400">
                    {{ step.index }}
                  </span>
                  <p class="text-sm font-semibold text-white">{{ step.title }}</p>
                </div>
                <p class="mt-3 text-sm leading-6 text-steel-300">{{ step.body }}</p>
              </div>
            </div>
          </article>

          <article class="home-panel rounded-[2rem] p-6">
            <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">{{ $t('home.closingEyebrow') }}</p>
            <h3 class="mt-3 font-display text-2xl font-semibold text-white">{{ $t('home.closingTitle') }}</h3>
            <p class="mt-4 text-sm leading-6 text-steel-200/90">{{ $t('home.closingDescription') }}</p>
            <div class="mt-6 flex flex-wrap gap-3">
              <router-link
                class="rounded-2xl bg-white px-5 py-3 text-xs font-semibold uppercase tracking-[0.26em] text-midnight-900 transition hover:bg-neon-400"
                to="/register"
              >
                {{ $t('home.becomeMember') }}
              </router-link>
              <router-link
                class="rounded-2xl border border-white/15 bg-white/5 px-5 py-3 text-xs font-semibold uppercase tracking-[0.26em] text-white transition hover:border-white/30 hover:bg-white/10"
                to="/login"
              >
                {{ $t('home.memberLogin') }}
              </router-link>
            </div>
          </article>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import VerifiedBadge from '@/components/VerifiedBadge.vue'
import { getAvatarUrl, getPostImageUrl, runSupabaseQueryWithRetry, supabase } from '@/lib/supabase'

const eventTypeLabels = {
  meet: 'Meet',
  drive: 'Drive',
  track: 'Track Day',
  cars_and_coffee: 'Cars & Coffee',
}

const garageBuildStatusLabels = {
  building: 'Building',
  street: 'Street',
  track: 'Track',
  show: 'Show',
  sale: 'For Sale',
}

export default {
  name: 'HomeView',
  components: {
    VerifiedBadge,
  },
  data() {
    return {
      contentLoading: true,
      homepagePosts: [],
      garageBuilds: [],
      localMeets: [],
    }
  },
  computed: {
    heroChips() {
      return [
        this.$t('home.chipMembers'),
        this.$t('home.chipBuilds'),
        this.$t('home.chipRoutes'),
      ]
    },
    heroStats() {
      return [
        {
          label: this.$t('home.statOneLabel'),
          value: this.$t('home.statOneValue'),
          caption: this.$t('home.statOneCaption'),
        },
        {
          label: this.$t('home.statTwoLabel'),
          value: this.$t('home.statTwoValue'),
          caption: this.$t('home.statTwoCaption'),
        },
        {
          label: this.$t('home.statThreeLabel'),
          value: this.$t('home.statThreeValue'),
          caption: this.$t('home.statThreeCaption'),
        },
      ]
    },
    topMembers() {
      const start = new Date()
      start.setHours(0, 0, 0, 0)
      start.setDate(start.getDate() - start.getDay() + 1)

      const members = new Map()
      this.homepagePosts
        .filter((post) => new Date(post.created_at) >= start)
        .forEach((post) => {
          const existing = members.get(post.user_id) || {
            id: post.user_id,
            handle: post.authorHandle,
            fullName: post.authorName,
            avatar: post.authorAvatar,
            initials: post.authorInitials,
            verified: post.authorVerified,
            score: 0,
            likes: 0,
          }
          existing.score += post.weekScore
          existing.likes += Number(post.likeCount || 0)
          members.set(post.user_id, existing)
        })

      return Array.from(members.values())
        .sort((a, b) => b.score - a.score)
        .slice(0, 5)
        .map((member, index) => ({
          ...member,
          rank: index + 1,
        }))
    },
    buildOfWeek() {
      const candidates = this.homepagePosts.filter((post) => !post.is_for_sale)
      if (!candidates.length) return null
      return [...candidates].sort((a, b) => {
        if (b.weekScore !== a.weekScore) return b.weekScore - a.weekScore
        return new Date(b.created_at) - new Date(a.created_at)
      })[0]
    },
    featuredGarageBuild() {
      return this.garageBuilds[0] || null
    },
    latestLocalMeets() {
      return this.localMeets.slice(0, 3)
    },
    featureCards() {
      return [
        {
          eyebrow: this.$t('home.featureFeedEyebrow'),
          tag: this.$t('home.featureFeedTag'),
          title: this.$t('home.featureFeedTitle'),
          body: this.$t('home.featureFeedBody'),
          points: [
            this.$t('home.featureFeedPointOne'),
            this.$t('home.featureFeedPointTwo'),
            this.$t('home.featureFeedPointThree'),
          ],
        },
        {
          eyebrow: this.$t('home.featureProfileEyebrow'),
          tag: this.$t('home.featureProfileTag'),
          title: this.$t('home.featureProfileTitle'),
          body: this.$t('home.featureProfileBody'),
          points: [
            this.$t('home.featureProfilePointOne'),
            this.$t('home.featureProfilePointTwo'),
            this.$t('home.featureProfilePointThree'),
          ],
        },
        {
          eyebrow: this.$t('home.featureMeetEyebrow'),
          tag: this.$t('home.featureMeetTag'),
          title: this.$t('home.featureMeetTitle'),
          body: this.$t('home.featureMeetBody'),
          points: [
            this.$t('home.featureMeetPointOne'),
            this.$t('home.featureMeetPointTwo'),
            this.$t('home.featureMeetPointThree'),
          ],
        },
      ]
    },
    storytellingCards() {
      return [
        {
          eyebrow: this.$t('home.storyCardOneEyebrow'),
          title: this.$t('home.storyCardOneTitle'),
          badge: this.$t('home.storyCardOneBadge'),
          body: this.$t('home.storyCardOneBody'),
        },
        {
          eyebrow: this.$t('home.storyCardTwoEyebrow'),
          title: this.$t('home.storyCardTwoTitle'),
          badge: this.$t('home.storyCardTwoBadge'),
          body: this.$t('home.storyCardTwoBody'),
        },
        {
          eyebrow: this.$t('home.storyCardThreeEyebrow'),
          title: this.$t('home.storyCardThreeTitle'),
          badge: this.$t('home.storyCardThreeBadge'),
          body: this.$t('home.storyCardThreeBody'),
        },
      ]
    },
    membershipSteps() {
      return [
        {
          index: '01',
          title: this.$t('home.stepOneTitle'),
          body: this.$t('home.stepOneBody'),
        },
        {
          index: '02',
          title: this.$t('home.stepTwoTitle'),
          body: this.$t('home.stepTwoBody'),
        },
        {
          index: '03',
          title: this.$t('home.stepThreeTitle'),
          body: this.$t('home.stepThreeBody'),
        },
      ]
    },
  },
  async mounted() {
    await this.fetchHomepageContent()
  },
  methods: {
    initials(value, fallback = 'D') {
      const prepared = String(value || '').trim()
      if (!prepared) return fallback
      return prepared
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((chunk) => chunk[0].toUpperCase())
        .join('') || fallback
    },
    currentMedia(post) {
      return post.mediaUrls?.[0] || post.imageUrl || '/drvn.jpg'
    },
    currentMediaPosition(post) {
      const offset = post.mediaOffsets?.[0] || { x: 50, y: 50 }
      return `${offset.x}% ${offset.y}%`
    },
    formatDateLabel(value) {
      if (!value) return 'Date TBD'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return 'Date TBD'
      return parsed.toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
      })
    },
    garageCarTitle(car) {
      return [car.make, car.model].filter(Boolean).join(' ') || this.$t('home.garageFallbackTitle')
    },
    garagePowerLabel(car) {
      return car.horsepower_tuned || car.horsepower_stock
        ? `${car.horsepower_tuned || car.horsepower_stock} hp`
        : this.$t('home.garageFallbackPower')
    },
    buildMeetImageUrl({ cover_image_path, cover_image_url }) {
      if (cover_image_path) {
        return getPostImageUrl({ imagePath: cover_image_path, imageUrl: cover_image_url })
      }
      return cover_image_url || ''
    },
    async fetchHomepageContent() {
      this.contentLoading = true

      try {
        await Promise.all([
          this.fetchHomepagePosts(),
          this.fetchGarageBuilds(),
          this.fetchLocalMeets(),
        ])
      } finally {
        this.contentLoading = false
      }
    },
    async fetchHomepagePosts() {
      const postsResult = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('posts')
          .select('id, user_id, caption, car_make, car_model, image_url, image_path, image_offset_x, image_offset_y, created_at, is_for_sale, post_media (image_url, image_path, sort_order, offset_x, offset_y)')
          .order('created_at', { ascending: false })
          .limit(24)
      ))

      if (postsResult.error || !postsResult.data?.length) {
        this.homepagePosts = []
        return
      }

      const rawPosts = postsResult.data || []
      const postIds = rawPosts.map((post) => post.id)
      const userIds = [...new Set(rawPosts.map((post) => post.user_id).filter(Boolean))]

      const [profilesResult, likesResult, reactionsResult, viewsResult] = await Promise.all([
        runSupabaseQueryWithRetry(() => (
          supabase
            .from('profiles')
            .select('id, handle, full_name, avatar_url, avatar_path, verified')
            .in('id', userIds)
        )),
        runSupabaseQueryWithRetry(() => (
          supabase
            .from('post_likes')
            .select('post_id')
            .in('post_id', postIds)
        )),
        runSupabaseQueryWithRetry(() => (
          supabase
            .from('post_reactions')
            .select('post_id')
            .in('post_id', postIds)
        )),
        runSupabaseQueryWithRetry(() => (
          supabase
            .from('post_views')
            .select('post_id')
            .in('post_id', postIds)
        )),
      ])

      const profileMap = new Map((profilesResult.data || []).map((profile) => [profile.id, profile]))
      const likeCounts = new Map()
      const reactionCounts = new Map()
      const viewCounts = new Map()

      ;(likesResult.data || []).forEach((row) => {
        likeCounts.set(row.post_id, (likeCounts.get(row.post_id) || 0) + 1)
      })
      ;(reactionsResult.data || []).forEach((row) => {
        reactionCounts.set(row.post_id, (reactionCounts.get(row.post_id) || 0) + 1)
      })
      ;(viewsResult.data || []).forEach((row) => {
        viewCounts.set(row.post_id, (viewCounts.get(row.post_id) || 0) + 1)
      })

      this.homepagePosts = rawPosts.map((post) => {
        const profile = profileMap.get(post.user_id) || {}
        const mediaItems = [
          {
            url: post.image_path
              ? getPostImageUrl({ imagePath: post.image_path, imageUrl: post.image_url })
              : post.image_url,
            offset: {
              x: Number.isFinite(post.image_offset_x) ? post.image_offset_x : 50,
              y: Number.isFinite(post.image_offset_y) ? post.image_offset_y : 50,
            },
          },
          ...(post.post_media || []).map((item) => ({
            url: item.image_path
              ? getPostImageUrl({ imagePath: item.image_path, imageUrl: item.image_url })
              : item.image_url,
            offset: {
              x: Number.isFinite(item.offset_x) ? item.offset_x : 50,
              y: Number.isFinite(item.offset_y) ? item.offset_y : 50,
            },
          })),
        ].filter((item) => Boolean(item.url))

        const likeCount = likeCounts.get(post.id) || 0
        const totalReactionCount = reactionCounts.get(post.id) || 0
        const viewCount = viewCounts.get(post.id) || 0
        const fullName = profile.full_name || 'DRVN Member'

        return {
          ...post,
          imageUrl: mediaItems[0]?.url || '',
          mediaUrls: mediaItems.map((item) => item.url),
          mediaOffsets: mediaItems.map((item) => item.offset),
          likeCount,
          totalReactionCount,
          viewCount,
          weekScore: likeCount * 4 + totalReactionCount * 3 + viewCount,
          authorName: fullName,
          authorHandle: profile.handle || 'member',
          authorAvatar: getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url }),
          authorInitials: this.initials(fullName),
          authorVerified: Boolean(profile.verified),
          carLabel: [post.car_make, post.car_model].filter(Boolean).join(' '),
        }
      })
    },
    async fetchGarageBuilds() {
      const garageResult = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('garage_cars')
          .select('id, user_id, make, model, year, trim, engine, drivetrain, horsepower_stock, horsepower_tuned, build_status, cover_image_url, cover_image_path, created_at, owner:profiles!garage_cars_user_id_fkey (handle, full_name, avatar_url, avatar_path, verified)')
          .order('created_at', { ascending: false })
          .limit(6)
      ))

      if (garageResult.error || !garageResult.data?.length) {
        this.garageBuilds = []
        return
      }

      this.garageBuilds = (garageResult.data || []).map((car) => {
        const ownerName = car.owner?.full_name || 'DRVN Member'
        return {
          ...car,
          title: this.garageCarTitle(car),
          subtitle: car.trim || car.engine || this.$t('home.garageFallbackSubtitle'),
          powerLabel: this.garagePowerLabel(car),
          drivetrainLabel: car.drivetrain || this.$t('home.garageFallbackDrivetrain'),
          buildStatusLabel: garageBuildStatusLabels[car.build_status] || this.$t('home.garageFallbackStatus'),
          coverImage: car.cover_image_path
            ? getPostImageUrl({ imagePath: car.cover_image_path, imageUrl: car.cover_image_url })
            : car.cover_image_url || '',
          ownerName,
          ownerHandle: car.owner?.handle || 'member',
          ownerAvatar: getAvatarUrl({ avatarPath: car.owner?.avatar_path, avatarUrl: car.owner?.avatar_url }),
          ownerVerified: Boolean(car.owner?.verified),
          ownerInitials: this.initials(ownerName),
        }
      })
    },
    async fetchLocalMeets() {
      const nowIso = new Date().toISOString()
      const meetsResult = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('local_meets')
          .select('id, host_user_id, title, event_type, city, country, venue, starts_at, cover_image_url, cover_image_path, cover_offset_x, cover_offset_y, status, host:profiles!local_meets_host_user_id_fkey (handle, full_name, avatar_url, avatar_path, verified)')
          .eq('status', 'scheduled')
          .gte('starts_at', nowIso)
          .order('starts_at', { ascending: true })
          .limit(3)
      ))

      if (meetsResult.error || !meetsResult.data?.length) {
        this.localMeets = []
        return
      }

      this.localMeets = (meetsResult.data || []).map((meet) => {
        const hostName = meet.host?.full_name || 'DRVN Member'
        return {
          ...meet,
          coverImage: this.buildMeetImageUrl(meet),
          coverPosition: `${Number.isFinite(meet.cover_offset_x) ? meet.cover_offset_x : 50}% ${Number.isFinite(meet.cover_offset_y) ? meet.cover_offset_y : 50}%`,
          eventTypeLabel: eventTypeLabels[meet.event_type] || 'Meet',
          startsAtLabel: this.formatDateLabel(meet.starts_at),
          locationLabel: [meet.city, meet.country].filter(Boolean).join(', ') || this.$t('home.meetsFallbackLocation'),
          hostHandle: meet.host?.handle || 'member',
          hostAvatar: getAvatarUrl({ avatarPath: meet.host?.avatar_path, avatarUrl: meet.host?.avatar_url }),
          hostInitials: this.initials(hostName),
        }
      })
    },
  },
}
</script>

<style scoped>
.home-shell {
  background:
    radial-gradient(circle at top left, rgba(125, 249, 255, 0.08), transparent 26%),
    radial-gradient(circle at top right, rgba(251, 191, 36, 0.08), transparent 24%),
    linear-gradient(180deg, rgba(10, 10, 11, 0.98), rgba(10, 10, 11, 1));
}

.home-panel {
  border: 1px solid rgba(255, 255, 255, 0.08);
  background:
    linear-gradient(160deg, rgba(17, 24, 39, 0.86), rgba(10, 10, 11, 0.92));
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.36);
}

.home-panel-featured {
  background:
    radial-gradient(circle at top left, rgba(251, 191, 36, 0.18), transparent 28%),
    radial-gradient(circle at bottom right, rgba(125, 249, 255, 0.12), transparent 24%),
    linear-gradient(155deg, rgba(15, 23, 42, 0.96), rgba(10, 10, 11, 0.92));
}

.theme-light .home-shell {
  background:
    radial-gradient(circle at top left, rgba(14, 165, 233, 0.08), transparent 24%),
    radial-gradient(circle at top right, rgba(245, 158, 11, 0.08), transparent 24%),
    linear-gradient(180deg, #f5f7fb, #eef2f7);
}

.theme-light .home-panel {
  border-color: rgba(148, 163, 184, 0.24);
  background:
    linear-gradient(160deg, rgba(255, 255, 255, 0.96), rgba(241, 245, 249, 0.94));
  box-shadow: 0 18px 50px rgba(148, 163, 184, 0.16);
}

.theme-light .home-panel-featured {
  background:
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.15), transparent 30%),
    radial-gradient(circle at bottom right, rgba(14, 165, 233, 0.1), transparent 25%),
    linear-gradient(160deg, rgba(255, 255, 255, 0.98), rgba(237, 242, 247, 0.96));
}

.theme-light .home-rank-row {
  background: rgba(255, 255, 255, 0.72) !important;
  border-color: rgba(148, 163, 184, 0.24) !important;
  box-shadow: 0 8px 24px rgba(148, 163, 184, 0.12);
}

.theme-light .home-rank-row:hover {
  border-color: rgba(59, 130, 246, 0.22) !important;
  background: rgba(255, 255, 255, 0.88) !important;
}
</style>
